// File: src/App.jsx

import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';

// استيراد إعدادات i18n
import './i18n';

import Header                  from "./components/Header";

// استيراد الخطوط
import "@fontsource/ibm-plex-sans-arabic/400.css";
import "@fontsource/ibm-plex-sans-arabic/500.css";
import "@fontsource/ibm-plex-sans-arabic/600.css";
import "@fontsource/ibm-plex-sans-arabic/700.css";
import { buildAppTheme } from "./theme";

//import "./assets/fonts/fonts.css";

// Warehouses
import BranchesPage from "./pages/Branches/BranchesPage";
import AddStock                from "./pages/Warehouses/AddStock";
import AddCategory             from "./pages/Warehouses/AddCategory";
import AddUnits                from "./pages/Warehouses/AddUnits";
import AddProduct              from "./pages/Warehouses/AddProduct";
import EditProductForm         from "./pages/Warehouses/EditProductForm";
import ProductList             from "./pages/Warehouses/productList";
import ProductMovementReport   from "./pages/Warehouses/Reports/ProductMovementReport";
import InventoryReport         from "./pages/Warehouses/Reports/InventoryReport"
import AggregatedReport  from "./pages/Warehouses/Reports/AggregatedReport"
// Settings & Definitions
import VatTypesPage            from "./pages/Services/VatTypesPage";
import ProgramSettingsPage     from "./pages/Services/ProgramSettingsPage";
import PaymentMethodsPage      from "./pages/Services/PaymentMethodsPage";
import CompanyProfileForm      from "./pages/Services/CompanyProfileForm";
import VouchersListPage from "./pages/Vouchers/VouchersListPage";
import CustomerReceiptForm from "./pages/Vouchers/CustomerReceiptForm";
import SupplierPaymentForm from "./pages/Vouchers/SupplierPaymentForm";
import GeneralReceiptForm from "./pages/Vouchers/GeneralReceiptForm";
import GeneralPaymentForm from "./pages/Vouchers/GeneralPaymentForm";
import TaxPaymentForm from "./pages/Vouchers/TaxPaymentForm";


import Importpage from "./pages/Services/ImportPage";
import Login                   from "./pages/Services/Login";
import Register                from "./pages/Services/Register";
import Dashboard               from "./pages/Services/Dashboard";
// Customers & Suppliers
import CustomerList            from "./pages/customerSuplier/CustomerList";
import CustomerForm            from "./pages/customerSuplier/CustomerForm";
import SupplierList            from "./pages/customerSuplier/SupplierList";
import SupplierForm            from "./pages/customerSuplier/SupplierForm";

// Invoices
import InvoiceForm             from "./pages/Invoices/Invoicesales";
import Pos                     from "./pages/POS/Pos";
import InvoiceTypesPage        from "./pages/Invoices/InvoiceTypesPage";
import AllInvoicesReport       from "./pages/Invoices/AllInvoicesReport";
import InvoicePrint            from "./pages/Invoices/InvoicePrint";
import SalesItemsSummary       from "./pages/Invoices/Report/SalesItemsSummary";
import SalesByDayReport        from "./pages/Invoices/Report/SalesByDayReport";
import ZatcaReport             from "./pages/Invoices/Report/ZatcaReport"; // Import Zatca Report
import InvoicePrintPos         from "./pages/POS/InvoicePrintPos";
import PosSessionReport        from "./pages/POS/PosSessionReport";
import PrintLayoutEditor       from "./pages/Invoices/PrintLayoutEditor";
// Accounts
import BanksList               from "./pages/Account/BanksList";
import BankForm                from "./pages/Account/BankForm";
import SafesList               from "./pages/Account/SafesList";
import SafeForm                from "./pages/Account/SafeForm";
import ChartOfAccountsPage     from "./pages/Account/ChartOfAccounts/ChartOfAccountsPage";


import SalesMenu               from "./pages/PageMenu/SalesMenu";
import WarehousesMenu         from "./pages/PageMenu/WarehousesMenu";
import Purchasesmenu          from "./pages/PageMenu/purchasesmenu";
import CompanyDefinitions  from "./pages/PageMenu/company-definitions";
import SettingsMenu from "./pages/PageMenu/SettingsMenu";
import AccountsMenu from './pages/PageMenu/AccountsMenu';
import VatReportPage from './pages/Account/VatReportPage';
import GlSettingsPage from './pages/Services/GlSettingsPage'; // Import the new page
import ZatcaSettingsPage from './pages/Services/ZatcaSettingsPage'; // Import ZATCA Settings
import PosSettingsPage from './pages/Services/PosSettingsPage'; // Import POS Settings

// users
import UserForm from "./pages/Users/UserForm"
import ChangePassword from "./pages/Users/ChangePassword";
import ChangeUserName from "./pages/Users/ChangeUserName";
import ChangeMobile from "./pages/Users/ChangeMobile";
import RolesPage from "./pages/Security/RolesPage";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LedgerReport from "./pages/Account/LedgerReport";
import AccountStatement from "./pages/Account/AccountStatement";
import TrialBalanceReport from "./pages/Account/TrialBalanceReport";
import DailyMovementReport from "./pages/Account/DailyMovementReport";
import IncomeStatementReport from "./pages/Account/IncomeStatementReport";
import BalanceSheetReport from "./pages/Account/BalanceSheetReport";
import JournalEntriesList from "./pages/Account/JournalEntries/JournalEntriesList";
import JournalEntryForm from "./pages/Account/JournalEntries/JournalEntryForm";

import { AuthProvider } from "./context/AuthContext"; // ✅
import { isTokenValid, isSessionActive } from "./utils/tokenUtils";

import ForgotPassword from "./pages/Users/ForgotPassword"; 
import ResetPassword from  "./pages/Users/ResetPassword";

// إنشاء cache للـ RTL
const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// إنشاء cache للـ LTR
const ltrCache = createCache({
  key: 'muiltr',
  stylisPlugins: [prefixer],
});

// دالة للتحقق من صلاحية الجلسة (التوكن ساري + لم يتم تجاوز مدة عدم الاستخدام 30 دقيقة)
function isAuthenticated() {
  const token = localStorage.getItem("token");
  return isTokenValid(token) && isSessionActive();
}

// مسار محمي: إذا لم يكن مسجّلًا أو انتهت صلاحية التوكن أو تجاوز مدة الخمول
function ProtectedRoute({ children }) {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

export default function App() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const theme = React.useMemo(() => buildAppTheme(isRTL), [isRTL]);

  // تطبيق اتجاه النص على body
  React.useEffect(() => {
    document.body.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  return (
    <CacheProvider value={isRTL ? rtlCache : ltrCache}>
      <AuthProvider> {/* ✅ Wrap with AuthProvider */}
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Routes>
          {/* صفحات عامة: تسجيل الدخول والتسجيل واستعادة كلمة المرور */}
<Route path="/" element={<Login />} />
<Route path="/login" element={<Login />} />
<Route path="/register" element={<Register />} />
<Route path="/forgot-password" element={<ForgotPassword />} />
<Route path="/reset-password" element={<ResetPassword />} />

          {/* جميع المسارات المحمية تقع ضمن الـ Header */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Header />
                <Routes>
                  {/* Dashboard */}
                  <Route path="dashboard" element={<Dashboard />} />

                  {/* Warehouses */}
                  <Route path="add-stock" element={<AddStock />} />
                  <Route path="add-category" element={<AddCategory />} />
                  <Route path="add-category/:id" element={<AddCategory />} />
                  <Route path="edit-category/:id" element={<AddCategory />} />
                  <Route path="add-unit" element={<AddUnits />} />
                  <Route path="add-product" element={<AddProduct />} />
                  <Route path="productList" element={<ProductList />} />
                  <Route path="edit-product/:id" element={<EditProductForm />} />
                  <Route path="reports/all-products-movement" element={<ProductMovementReport />} />
                  <Route path="reports/InventoryReport" element={<InventoryReport/>}/>
                  <Route path="reports/AggregatedReport" element={<AggregatedReport/>}/>
                  
                  {/* Settings & Definitions */}
                  <Route path="vat-types" element={<VatTypesPage />} />
                  <Route path="/add-importpage" element={<Importpage />} />
                  <Route path="settings" element={<ProgramSettingsPage />} /> 
                  <Route path="zatca-settings" element={<ZatcaSettingsPage />} />
                  <Route path="gl-settings" element={<GlSettingsPage />} /> {/* Route for GL Settings */}
                  <Route path="pos-settings" element={<PosSettingsPage />} /> {/* Route for POS Settings */}
                  
                  {/* Customers & Suppliers */}
                  <Route path="customers" element={<CustomerList />} />
                  <Route path="customers/new" element={<CustomerForm />} />
                  <Route path="customers/:id/edit" element={<CustomerForm />} />
                  <Route path="Supplier" element={<SupplierList />} />
                  <Route path="Supplier/new" element={<SupplierForm />} />
                  <Route path="Supplier/:id/edit" element={<SupplierForm />} />

                  {/* Invoices */}
                  <Route path="invoices/new/:typeId" element={<InvoiceForm />} />
                  <Route path="invoices/new" element={<InvoiceForm />} />
                  <Route path="invoice-types" element={<InvoiceTypesPage />} />
                  <Route path="pos" element={<Pos />} />
                  <Route path="reports/all-invoices" element={<AllInvoicesReport />} />
                  <Route path="invoices/view/:id" element={<InvoiceForm />} />
                  <Route path="invoices/edit/:id" element={<InvoiceForm />} />
                  <Route path="/invoices/print/:id" element={<InvoicePrint />} />
                  <Route path="/reports/aggregated-sales-items" element={<SalesItemsSummary />} />
                  <Route path="/reports/aggregated-sales-ByDay" element={<SalesByDayReport />} />
                  <Route path="/reports/zatca" element={<ZatcaReport />} /> {/* Route for Zatca Report */}

                  <Route path="/invoices/printPos/:invoiceId" element={<InvoicePrintPos />} />
                  <Route path="/reports/pos-session/:sessionId" element={<PosSessionReport />} />
                  <Route path="/print-layout" element={<PrintLayoutEditor />} />
                  {/* Accounts */}
                  <Route path="banks" element={<BanksList />} />
                  <Route path="banks/new" element={<BankForm />} />
                  <Route path="banks/:id" element={<BankForm />} />
                  <Route path="safes" element={<SafesList />} />
                  <Route path="safes/new" element={<SafeForm />} />
                  <Route path="safes/:id" element={<SafeForm />} />
                  <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
                  <Route path="payment-methods" element={<PaymentMethodsPage />} />
                  <Route path="company/profile" element={<CompanyProfileForm />} />
                  <Route path="change-password" element={<ChangePassword />} />
                  <Route path="change-username" element={<ChangeUserName />} />
                  <Route path="change-mobile" element={<ChangeMobile />} />
                  <Route path="UserForm" element={<UserForm />}/>
                  <Route path="roles" element={<RolesPage />}/>
                  <Route path="/VatReportPage" element={<VatReportPage />}/>
                  
                  {/* Menus */}
                  <Route path="sales-menu" element={<SalesMenu />} />
                  <Route path="purchases-menu" element={<Purchasesmenu />} />
                  <Route path="warehouses-menu" element={<WarehousesMenu />} />
                  <Route path="accounts-menu" element={<AccountsMenu />} />
                  <Route path="company-definitions" element={<CompanyDefinitions />} />
                  <Route path="settings-menu" element={<SettingsMenu />} />
                  <Route path="branches" element={<BranchesPage />} />
                  
                  {/* Vouchers */}
                  <Route path="vouchers-list" element={<VouchersListPage />} />
                  
                  {/* Customer Receipt */}
                  <Route path="vouchers/customer-receipt" element={<CustomerReceiptForm />} />
                  <Route path="vouchers/customer-receipt/:id/edit" element={<CustomerReceiptForm />} />
                  <Route path="vouchers/customer-receipt/:id/view" element={<CustomerReceiptForm />} />
                  
                  {/* Supplier Payment */}
                  <Route path="vouchers/supplier-payment" element={<SupplierPaymentForm />} />
                  <Route path="vouchers/supplier-payment/:id/edit" element={<SupplierPaymentForm />} />
                  <Route path="vouchers/supplier-payment/:id/view" element={<SupplierPaymentForm />} />
                  
                  {/* General Receipt */}
                  <Route path="vouchers/general-receipt" element={<GeneralReceiptForm />} />
                  <Route path="vouchers/general-receipt/:id/edit" element={<GeneralReceiptForm />} />
                  <Route path="vouchers/general-receipt/:id/view" element={<GeneralReceiptForm />} />
                  
                  {/* General Payment */}
                  <Route path="vouchers/general-payment" element={<GeneralPaymentForm />} />
                  <Route path="vouchers/general-payment/:id/edit" element={<GeneralPaymentForm />} />
                  <Route path="vouchers/general-payment/:id/view" element={<GeneralPaymentForm />} />
                  
                  {/* Tax Payment */}
                  <Route path="vouchers/tax-payment" element={<TaxPaymentForm />} />
                  <Route path="vouchers/tax-payment/:id/edit" element={<TaxPaymentForm />} />
                  <Route path="vouchers/tax-payment/:id/view" element={<TaxPaymentForm />} />

                  <Route path="/LedgerReport/ledgers" element={<LedgerReport />} />
                  <Route path="/financial-transactions" element={<LedgerReport />} />
                  <Route path="/reports/account-statement" element={<AccountStatement />} />
                  <Route path="/reports/trial-balance" element={<TrialBalanceReport />} />
                  <Route path="/reports/daily-movement" element={<DailyMovementReport />} />
                  <Route path="/reports/income-statement" element={<IncomeStatementReport />} />
                  <Route path="/reports/balance-sheet" element={<BalanceSheetReport />} />
                  
                  {/* Journal Entries */}
                  <Route path="journal-entries" element={<JournalEntriesList />} />
                  <Route path="journal-entries/new" element={<JournalEntryForm />} />
                  <Route path="journal-entries/:id/edit" element={<JournalEntryForm />} />
                  <Route path="journal-entries/:id/view" element={<JournalEntryForm />} />
                 

                  {/* 404 داخل المحميين */}
                  <Route
                    path="*"
                    element={
                      <div className="p-10 text-center text-red-500">
                        ❌ الصفحة غير موجودة
                      </div>
                    }
                  />
                </Routes>
                 
              </ProtectedRoute>
            }      
          />
                  </Routes>
          <ToastContainer position="top-center" />
        </ThemeProvider>
      </AuthProvider>
    </CacheProvider>
  );
}
